import {PanelBody, SelectControl} from '@wordpress/components';
import {__, sprintf} from '@wordpress/i18n';
import {SetAttributes} from './Edit';
import {Taxonomy} from '@wordpress/api/taxonomies';
import {useSelect} from '@wordpress/data';
import {range} from 'lodash';
import {CONFIG} from '../../../globals/config';

export type DisplayPostsOptions = {
	'display-posts': string;
	'display-posts/limit': number;
	'display-posts/post-type': string;
	'display-posts/order': string;
	'display-posts/order-by': string;
}

export type PostTypeOptions = {
	[ taxonomy: string ]: {
		[ postType: string ]: string
	}
};

type Props = {
	attributes: DisplayPostsOptions;
	setAttributes: SetAttributes;
	taxonomy?: Taxonomy<'edit'>;
};

/**
 * Get the post types available for a given taxonomy.
 *
 * @since 9.9.0
 */
export function getPostTypesOnTaxonomy( taxonomy: Taxonomy<'edit'> | undefined ) {
	if ( undefined === taxonomy ) {
		return [];
	}
	const options = CONFIG.displayPosts.types[ taxonomy.slug ] ?? {};
	return Object.entries( options ).map( ( [ value, label ] ) => ( {
		value,
		label,
	} ) );
}


const DisplayPosts = ( {attributes, setAttributes, taxonomy}: Props ) => {
	const postTypeValue = attributes[ 'display-posts/post-type' ];
	const postType = useSelect( select => {
		return select( 'core' ).getPostType( postTypeValue ?? 'post' );
	}, [ postTypeValue ] );

	return ( <PanelBody
		/* translators: %s: {post type plural label} */
		title={sprintf( __( 'Display %s', 'advanced-sidebar-menu' ), postType?.labels?.name ?? __( 'Posts', 'advanced-sidebar-menu' ) )}
		initialOpen={'' !== attributes[ 'display-posts' ]}
	>
		<SelectControl
			value={attributes[ 'display-posts' ]}
			/* translators: %1$s: {post type plural label} */
			label={sprintf( __( 'Display assigned %1$s under', 'advanced-sidebar-menu' ), postType?.labels?.name.toLowerCase() ?? '' )}
			options={Object.entries( CONFIG.displayPosts.options ).map( ( [ value, label ] ) => ( {
				value,
				label: sprintf( label, taxonomy?.labels?.name.toLowerCase() ?? '' ),
			} ) )}
			onChange={value => {
				setAttributes( {
					'display-posts': value,
				} );
			}} />

		{'' !== attributes[ 'display-posts' ] && <SelectControl
			value={postTypeValue}
			label={__( 'Post type', 'advanced-sidebar-menu' )}
			labelPosition={'side'}
			options={getPostTypesOnTaxonomy( taxonomy )}
			onChange={value => {
				setAttributes( {
					'display-posts/post-type': value,
				} );
			}} />}

		{'' !== attributes[ 'display-posts' ] && <div className={'components-base-control'}>
			{__( 'Display', 'advanced-sidebar-menu' )}&nbsp;
			<select
				value={attributes[ 'display-posts/limit' ]}
				onChange={ev => setAttributes( {
					'display-posts/limit': parseInt( ev.target.value ),
				} )}
			>
				<option value="100">
					{__( '- All -', 'advanced-sidebar-menu' )}
				</option>
				{range( 1, 10 ).map( n => <option key={n} value={n}>
					{n}
				</option> )}
			</select>
			&nbsp;{postType?.labels?.name.toLowerCase()}
		</div>}

		{'' !== attributes[ 'display-posts' ] && <SelectControl
			value={attributes[ 'display-posts/order-by' ]}
			/* translators: %s: {post type plural label} */
			label={sprintf( __( 'Order %s by', 'advanced-sidebar-menu' ), postType?.labels?.name.toLowerCase() ?? '' )}
			labelPosition={'top'}
			options={Object.entries( CONFIG.displayPosts.orderBy ).map( ( [ value, label ] ) => ( {
				value,
				label,
			} ) )}
			onChange={value => {
				setAttributes( {
					'display-posts/order-by': value,
				} );
			}} />}

		{'' !== attributes[ 'display-posts' ] && <SelectControl
			value={attributes[ 'display-posts/order' ]}
			/* translators: %s: {post type plural label} */
			label={sprintf( __( 'Order %s', 'advanced-sidebar-menu' ), postType?.labels?.name.toLowerCase() ?? '' )}
			labelPosition={'top'}
			options={[
				{
					value: 'ASC',
					label: __( 'Ascending', 'advanced-sidebar-menu' ),
				},
				{
					value: 'DESC',
					label: __( 'Descending', 'advanced-sidebar-menu' ),

				},
			]}
			onChange={value => {
				setAttributes( {
					'display-posts/order': value,
				} );
			}} />}

	</PanelBody> );
};

export default DisplayPosts;
